// InteractionForm — Add note/email/meeting modal
import { useState } from 'react'
import { X } from 'lucide-react'
import { createCrmInteraction } from '../../services/api'
import type { CrmInteraction } from '../../types'

interface Props {
  projectId: string
  targetType: 'contact' | 'organisation'
  targetId: string
  onClose: () => void
  onCreated: (interaction: CrmInteraction) => void
}

export default function InteractionForm({ projectId, targetType, targetId, onClose, onCreated }: Props) {
  const [interaction_type, setType] = useState<CrmInteraction['interaction_type']>('note')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      const result = await createCrmInteraction({
        project_id: projectId,
        target_type: targetType,
        target_id: targetId,
        interaction_type,
        subject,
        body,
      })
      onCreated(result)
    } catch {
      alert('Failed to create interaction')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-base font-semibold text-gray-900">Add Interaction</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select value={interaction_type} onChange={e => setType(e.target.value as CrmInteraction['interaction_type'])}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500">
              <option value="note">Note</option>
              <option value="email">Email</option>
              <option value="meeting">Meeting</option>
              <option value="call">Call</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Details</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600 disabled:opacity-50">
              {saving ? 'Saving...' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
